<template>
  <div class="auth-page">

    <!-- Left branding panel -->
    <div class="auth-brand">
      <div class="auth-brand__inner">
        <router-link to="/" class="auth-logo">
          <img v-if="company && company.logo" :src="imgUrl + 'logo/' + company.logo" alt="Structurecity" />
          <span v-else class="auth-logo__text">Structure<span>city</span></span>
        </router-link>
        <div class="auth-brand__body">
          <h1>Set a new password</h1>
          <p>Choose a strong password to keep your Structurecity account secure.</p>
          <div class="auth-tips">
            <div class="auth-tip">
              <span class="auth-tip__icon">🔒</span>
              <span>Use at least 8 characters</span>
            </div>
            <div class="auth-tip">
              <span class="auth-tip__icon">🔡</span>
              <span>Mix letters, numbers &amp; symbols</span>
            </div>
            <div class="auth-tip">
              <span class="auth-tip__icon">🚫</span>
              <span>Avoid reusing old passwords</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Right form panel -->
    <div class="auth-panel">
      <div class="auth-panel__inner">

        <!-- Mobile logo -->
        <router-link to="/" class="auth-logo auth-logo--mobile">
          <img v-if="company && company.logo" :src="imgUrl + 'logo/' + company.logo" alt="Structurecity" />
          <span v-else class="auth-logo__text">Structure<span>city</span></span>
        </router-link>

        <!-- Invalid / expired link -->
        <div v-if="linkInvalid" class="verify-state">
          <div class="verify-icon verify-icon--error">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </div>
          <h2>Link expired</h2>
          <p>This password reset link is invalid or has already been used. Please request a new one.</p>
          <router-link to="/forgot-password" class="auth-btn">Request new link</router-link>
          <p class="auth-switch"><router-link to="/sign-in" class="auth-link">Back to sign in</router-link></p>
        </div>

        <!-- Success -->
        <div v-else-if="successMsg" class="verify-state">
          <div class="verify-icon verify-icon--success">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
          </div>
          <h2>Password updated!</h2>
          <p>Your password has been reset successfully. You can now sign in with your new password.</p>
          <router-link to="/sign-in" class="auth-btn">Sign in</router-link>
        </div>

        <!-- Form -->
        <template v-else>
          <div class="auth-panel__header">
            <div class="auth-panel__icon">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
            </div>
            <h2>Reset your password</h2>
            <p>Enter and confirm your new password below.</p>
          </div>

          <div v-if="errorMsg" class="sc-alert sc-alert--error">{{ errorMsg }}</div>

          <form @submit.prevent="submitForm">
            <div class="sc-field">
              <label>New password</label>
              <div class="sc-field__pw">
                <input :type="showPwd ? 'text' : 'password'" v-model="password" placeholder="Minimum 8 characters" required minlength="8" autocomplete="new-password" />
                <button type="button" class="sc-field__eye" @click="showPwd = !showPwd" tabindex="-1">
                  <svg v-if="!showPwd" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                </button>
              </div>
            </div>

            <div class="sc-field">
              <label>Confirm new password</label>
              <input :type="showPwd ? 'text' : 'password'" v-model="confirmPassword" placeholder="Repeat your password" required autocomplete="new-password" />
            </div>

            <button type="submit" class="auth-btn" :disabled="loading">
              <span v-if="loading" class="sc-spinner"></span>
              <span v-else>Set new password</span>
            </button>
          </form>

          <p class="auth-switch">
            Remembered it?
            <router-link to="/sign-in" class="auth-link auth-link--bold">Sign in</router-link>
          </p>

          <p class="auth-back"><router-link to="/" class="auth-link">← Back to home</router-link></p>
        </template>

      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'ResetPassword',

  data() {
    return {
      imgUrl: process.env.VUE_APP_IMAGE_URL || '',
      company: null,
      password: '',
      confirmPassword: '',
      showPwd: false,
      loading: false,
      errorMsg: '',
      successMsg: false,
      linkInvalid: false,
    };
  },

  watch: {
    $route: { immediate: true, handler(to) { document.title = to.meta && to.meta.title ? to.meta.title : 'Reset Password — Structurecity'; } },
  },

  created() {
    this.fetchCompany();
    this.validateLink();
  },

  methods: {
    validateLink() {
      axios.get(process.env.VUE_APP_API_URL + 'auth/reset-password/' + this.$route.params.id)
        .then(res => {
          if (res.status !== 200) this.linkInvalid = true;
        })
        .catch(() => { this.linkInvalid = true; });
    },

    submitForm() {
      if (this.password !== this.confirmPassword) {
        this.errorMsg = 'Passwords do not match.';
        return;
      }
      this.loading = true;
      this.errorMsg = '';
      const fd = new FormData();
      fd.append('password', this.password);
      fd.append('confirm_password', this.confirmPassword);
      axios.post(process.env.VUE_APP_API_URL + 'auth/reset-password/' + this.$route.params.id, fd)
        .then(res => {
          if (res.status === 200) {
            this.successMsg = true;
          } else {
            this.errorMsg = res.message || 'Could not reset password. Please try again.';
          }
        })
        .catch(() => { this.errorMsg = 'Could not reset password. Please try again.'; })
        .finally(() => { this.loading = false; });
    },

    fetchCompany() {
      axios(process.env.VUE_APP_API_URL + 'company')
        .then(res => { if (res && res.data) this.company = res.data; })
        .catch(() => {});
    },
  },
};
</script>

<style scoped>
*, *::before, *::after { box-sizing: border-box; }

.auth-page {
  display: grid; grid-template-columns: 1fr 1fr;
  min-height: 100vh;
}

/* ── Left branding panel ── */
.auth-brand {
  background: linear-gradient(145deg, #0a1628 0%, #1e3a5f 50%, #1a4a7a 100%);
  padding: 3rem; display: flex; align-items: center; justify-content: center;
  position: relative; overflow: hidden;
}
.auth-brand::before {
  content: ''; position: absolute; inset: 0; pointer-events: none;
  background-image: radial-gradient(circle, rgba(255,255,255,.05) 1px, transparent 1px);
  background-size: 36px 36px;
}
.auth-brand__inner {
  position: relative; z-index: 1; max-width: 420px; width: 100%;
  display: flex; flex-direction: column; gap: 2.5rem;
}

.auth-logo { display: flex; align-items: center; text-decoration: none; }
.auth-logo img { height: 42px; object-fit: contain; }
.auth-logo__text { font-size: 1.75rem; font-weight: 800; color: #fff; letter-spacing: -.03em; }
.auth-logo__text span { color: #f59e0b; }
.auth-logo--mobile { display: none; }

.auth-brand__body { display: flex; flex-direction: column; gap: 1.25rem; }
.auth-brand__body h1 {
  font-size: clamp(1.5rem, 2.5vw, 2rem); font-weight: 800; color: #fff;
  line-height: 1.2; letter-spacing: -.03em; margin: 0;
}
.auth-brand__body p { color: rgba(255,255,255,.65); font-size: 1rem; line-height: 1.65; margin: 0; }

.auth-tips { display: flex; flex-direction: column; gap: .875rem; margin-top: .5rem; }
.auth-tip {
  display: flex; align-items: center; gap: .875rem;
  background: rgba(255,255,255,.07); border: 1px solid rgba(255,255,255,.1);
  border-radius: 12px; padding: .875rem 1.125rem;
}
.auth-tip__icon { font-size: 1.25rem; flex-shrink: 0; }
.auth-tip span:last-child { color: rgba(255,255,255,.85); font-size: .9375rem; font-weight: 500; }

/* ── Right form panel ── */
.auth-panel {
  background: #fff; display: flex; align-items: center; justify-content: center;
  padding: 3rem 2rem;
}
.auth-panel__inner {
  width: 100%; max-width: 420px; display: flex; flex-direction: column; gap: 1.5rem;
}

.auth-panel__icon {
  width: 56px; height: 56px; border-radius: 14px;
  background: rgba(30,58,95,.08); color: #1e3a5f;
  display: flex; align-items: center; justify-content: center;
  margin-bottom: .25rem;
}
.auth-panel__header { display: flex; flex-direction: column; gap: .5rem; }
.auth-panel__header h2 {
  font-size: 1.875rem; font-weight: 800; color: #0f172a;
  margin: 0; letter-spacing: -.03em;
}
.auth-panel__header p { color: #64748b; font-size: 1rem; margin: 0; }

/* ── Fields ── */
.sc-field { display: flex; flex-direction: column; gap: .375rem; margin-bottom: 1rem; }
.sc-field label { font-size: .875rem; font-weight: 600; color: #334155; }
.sc-field input {
  width: 100%; padding: .6875rem 1rem;
  border: 1.5px solid #e2e8f0; border-radius: 10px;
  font-family: inherit; font-size: .9375rem; color: #0f172a;
  background: #fff; outline: none; transition: border-color .15s, box-shadow .15s;
}
.sc-field input:focus { border-color: #1e3a5f; box-shadow: 0 0 0 3px rgba(30,58,95,.1); }

.sc-field__pw { position: relative; }
.sc-field__pw input { padding-right: 2.75rem; }
.sc-field__eye {
  position: absolute; right: .75rem; top: 50%; transform: translateY(-50%);
  background: none; border: none; cursor: pointer; color: #94a3b8; padding: 0;
  display: flex; align-items: center; transition: color .15s;
}
.sc-field__eye:hover { color: #1e3a5f; }

/* ── Buttons ── */
.auth-btn {
  width: 100%; padding: .8125rem 1.5rem;
  background: #1e3a5f; color: #fff; border: none; border-radius: 10px;
  font-family: inherit; font-size: 1rem; font-weight: 600; cursor: pointer;
  display: flex; align-items: center; justify-content: center; gap: .5rem;
  text-decoration: none;
  transition: background .15s, transform .1s, box-shadow .15s;
}
.auth-btn:hover:not(:disabled) { background: #2a4f7c; box-shadow: 0 4px 16px rgba(30,58,95,.3); color: #fff; text-decoration: none; }
.auth-btn:active:not(:disabled) { transform: translateY(1px); }
.auth-btn:disabled { opacity: .6; cursor: not-allowed; }

.auth-link { color: #1e3a5f; text-decoration: none; font-size: .875rem; font-weight: 500; }
.auth-link:hover { text-decoration: underline; }
.auth-link--bold { font-weight: 700; }
.auth-switch { text-align: center; color: #64748b; font-size: .9rem; margin: 0; }
.auth-back { text-align: center; margin: 0; }
.auth-back .auth-link { color: #94a3b8; }

/* ── Alerts ── */
.sc-alert { padding: .75rem 1rem; border-radius: 8px; font-size: .875rem; }
.sc-alert--error { background: #fef2f2; color: #dc2626; border: 1px solid #fecaca; }

/* ── Spinner ── */
.sc-spinner {
  display: inline-block; width: 18px; height: 18px;
  border: 2px solid rgba(255,255,255,.3); border-top-color: #fff;
  border-radius: 50%; animation: spin .7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ── State screens ── */
.verify-state {
  display: flex; flex-direction: column; align-items: center;
  text-align: center; gap: 1.25rem;
}
.verify-icon {
  width: 72px; height: 72px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
}
.verify-icon--success { background: #f0fdf4; color: #16a34a; }
.verify-icon--error   { background: #fef2f2; color: #dc2626; }
.verify-state h2 { font-size: 1.875rem; font-weight: 800; color: #0f172a; margin: 0; letter-spacing: -.03em; }
.verify-state p { color: #64748b; font-size: 1rem; line-height: 1.65; margin: 0; max-width: 340px; }

/* ── Responsive ── */
@media (max-width: 900px) {
  .auth-page { grid-template-columns: 1fr; }
  .auth-brand { display: none; }
  .auth-logo--mobile { display: flex; }
  .auth-panel { padding: 2.5rem 1.5rem; min-height: 100vh; }
}
</style>
